import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { cx } from './tokens';

type Tone = 'ink' | 'red' | 'teal' | 'paper' | 'ghost';

const TONES: Record<Tone, string> = {
  ink: 'bg-ink text-paper border-ink',
  red: 'bg-red text-paper border-ink',
  teal: 'bg-teal text-paper border-ink',
  paper: 'bg-paper-raised text-ink border-ink',
  ghost: 'bg-paper/40 text-ink border-ink/45',
};

/**
 * `minH` keeps a grid of buttons on one baseline: Romanian labels differ enough
 * in length that some wrap to two lines and some do not, and without a floor
 * the grid comes out ragged.
 */
const SIZES = {
  sm: {
    face: 'px-3 py-2 text-[0.7rem] tracking-[0.1em] border-2 leading-[1.25]',
    press: '2px',
    minH: 'min-h-9',
  },
  md: {
    face: 'px-5 py-3 text-sm tracking-[0.12em] border-[3px] leading-[1.25]',
    press: '4px',
    minH: 'min-h-12',
  },
  lg: {
    face: 'px-7 py-4 text-base tracking-[0.12em] border-[3px] leading-[1.25]',
    press: '5px',
    minH: 'min-h-14',
  },
  vote: {
    face: 'px-6 py-9 text-4xl tracking-[0.16em] border-4 leading-none',
    press: '8px',
    minH: 'min-h-28',
  },
} as const;

/**
 * Controls acknowledge a tap on the frame they were tapped in — the press
 * translation is pure CSS and never awaits the server. Motion decorates the
 * display; it never gates input.
 */
export function Button({
  children,
  tone = 'paper',
  size = 'md',
  block = false,
  rule,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  tone?: Tone;
  size?: keyof typeof SIZES;
  block?: boolean;
  /** Inset deco hairline. On by default for the large vote targets. */
  rule?: boolean;
}) {
  const s = SIZES[size];
  const showRule = rule ?? size === 'vote';

  return (
    <button
      type="button"
      className={cx(
        'pressed group disabled:pointer-events-none disabled:opacity-40',
        block ? 'flex w-full' : 'inline-flex',
        className,
      )}
      style={{ '--press': s.press } as CSSProperties}
      {...rest}
    >
      <span
        className={cx(
          'press-face relative flex h-full w-full items-center justify-center text-center',
          'font-display uppercase',
          showRule && 'inner-rule',
          TONES[tone],
          s.face,
          s.minH,
        )}
      >
        {children}
      </span>
    </button>
  );
}
